import i18n from '@/utils/i18n';
import React from 'react';
import { Business } from '@/components/locations/types';
import { CONTAINER_MODAL_ROUTES } from '@/navigators/routes';
import { ContainerModalStackParams } from '@/navigators/types';
import { Coordinate } from '@usepepper/ui/molecules/map/types';
import { createStyles } from '@/contexts/theme-context';
import { FormInputContainer } from '@/components/form';
import { SelectInput } from '@/components/select-input';
import { SortOrder } from '@/hooks/use-nearby-businesses';
import { StackNavigationProp } from '@react-navigation/stack';
import { useNavigation } from '@react-navigation/native';

const DIRECT_HIT_RADIUS = '100';

// These are categorized by google for PlaceResults as 'region' or 'cities' types
// We can follow that guideline to determine if a PlaceResult is a business or a region
// See: https://developers.google.com/maps/documentation/places/web-service/supported_types#table1
const GOOGLE_REGION_TYPES = new Set<string>([
  'locality',
  'sublocality',
  'postal_code',
  'country',
  'administrative_area_level_1',
  'administrative_area_level_2',
  'administrative_area_level_3',
]);

/**
 * Determines whether the given PlaceResult should be treated as a business.
 *
 * If `placeResult.types` is null, undefined, or an empty array,
 * it defaults to a region-based search (i.e. returns false).
 *
 * Otherwise, if none of the types are among the region types, it returns true.
 */
export function isPlaceResultBusiness(
  placeResult: google.maps.places.PlaceResult | null,
): boolean {
  if (!Array.isArray(placeResult?.types) || placeResult.types.length === 0) {
    // When types is missing or empty, default to region (not a business).
    return false;
  }
  // If at least one of the types is a region type, treat it as a region.
  return !placeResult.types.some((type) => GOOGLE_REGION_TYPES.has(type));
}

interface Props {
  selectCurrentLocation: () => void;
  selectedLocationName?: string;
  selectLocation: (
    { latitude, longitude }: Coordinate,
    radius: string | null,
    sortOrder: SortOrder,
  ) => void;
  setMapChanged: (changed: boolean) => void;
  setSelectedBusiness: (business: Business | undefined) => void;
  setSelectedLocationName: (selectedLocationName?: string) => void;
  setShowLeadGeneratorList: (show: boolean) => void;
}

export const LeadGeneratorLocationSearch: React.FC<Props> = ({
  selectCurrentLocation,
  selectLocation,
  setMapChanged,
  setSelectedBusiness,
  selectedLocationName,
  setSelectedLocationName,
  setShowLeadGeneratorList,
}) => {
  const [styles] = useStyles();
  const { navigate } =
    useNavigation<StackNavigationProp<ContainerModalStackParams>>();

  const handleSelectLocation = () => {
    navigate(CONTAINER_MODAL_ROUTES.PlacesSearch, {
      initialValue: selectedLocationName,
      onSelect: (
        placeName: string,
        placeResult: google.maps.places.PlaceResult | null,
      ) => {
        setSelectedLocationName(placeName);
        if (
          placeResult?.geometry?.location?.lat &&
          placeResult?.geometry?.location?.lng
        ) {
          // Set radius and sort order based on place type
          const isPlaceBusiness = isPlaceResultBusiness(placeResult);
          const radius = isPlaceBusiness ? DIRECT_HIT_RADIUS : null;
          const sortOrder = isPlaceBusiness
            ? SortOrder.DISTANCE
            : SortOrder.BEST_MATCH;

          // Library typings incorrectly think location.lat/lng are functions
          // and we need to cast them to numbers so TS doesn't complain
          selectLocation(
            {
              latitude: placeResult.geometry.location.lat as unknown as number,
              longitude: placeResult.geometry.location.lng as unknown as number,
            },
            radius,
            sortOrder,
          );
        }
        // Clear the selected business when a new location is selected
        setSelectedBusiness(undefined);
        setMapChanged(false);
      },
      onSelectCurrentLocation: () => {
        selectCurrentLocation();
        setShowLeadGeneratorList(true);
      },
      placeholder: i18n.t('current_location'),
      types: ['geocode', 'establishment'],
    });
    setShowLeadGeneratorList(false);
  };

  return (
    <FormInputContainer>
      <SelectInput
        inputContainerStyle={styles.textInputContainer}
        label=""
        onPress={handleSelectLocation}
        value={selectedLocationName || i18n.t('current_location')}
      />
    </FormInputContainer>
  );
};

const useStyles = createStyles((theme) => ({
  textInputContainer: {
    backgroundColor: theme.colors.background,
    borderColor: theme.colors.divider,
    borderRadius: 8,
    borderWidth: 1,
    fontSize: 16,
    fontWeight: '600',
    height: 48,
    marginLeft: 16,
    marginRight: 16,
  },
}));
